import { usePaywallStates } from '@/atoms/paywallAtoms';
import { Button } from '@heidi/design-system/button';
import { TypographyP2 } from '@heidi/design-system/typography';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isOnPrem } from '@/utils/buildProfile';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QUOTA_UPGRADE_CTA_TEXT } from '../constants/quotaPaywall';

/**
 * Renders a quota-exceeded message with an inline "Upgrade your plan to Clinician"
 * CTA link. The CTA text within the message becomes a clickable underlined link
 * that navigates to `upgradeUrl` when provided, otherwise opens the Clinician
 * upgrade modal (which handles team-member permissions).
 */
export function QuotaExceededMessage({
  content,
  upgradeUrl,
}: {
  content: string;
  upgradeUrl?: string;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const { toggleClinicianUpgradeModal } = usePaywallStates();

  const handleUpgradeClick = useCallback(() => {
    if (upgradeUrl) {
      void router.push(upgradeUrl);
      return;
    }
    toggleClinicianUpgradeModal(true);
  }, [router, toggleClinicianUpgradeModal, upgradeUrl]);

  const ctaIndex = content.indexOf(QUOTA_UPGRADE_CTA_TEXT);

  // ON-PREM-GATE: an appliance is deployment-licensed, so there is no plan to
  // upgrade to and nothing behind the link (the modal is cloud-only, F6). The
  // on-prem engine never emits a quota chunk, but this component is also reached
  // by a substring match on the ANSWER TEXT (`ChatTimeline`), and a local model
  // can write that sentence — it must then read as plain text.
  if (ctaIndex === -1 || isAppleCompliantBuild() || isOnPrem) {
    return <TypographyP2>{content}</TypographyP2>;
  }

  const before = content.slice(0, ctaIndex);
  const after = content.slice(ctaIndex + QUOTA_UPGRADE_CTA_TEXT.length);

  return (
    <TypographyP2>
      {before}
      <Button
        variant="link"
        size="sm"
        className="inline h-auto p-0 text-text-secondary underline hover:text-text-secondary"
        onClick={handleUpgradeClick}
      >
        {t('quota.upgradeToClinician')}
      </Button>
      {after}
    </TypographyP2>
  );
}
